import { api, auth$, getCurrentUser, Profile } from '@creditsights/api-ui-web';
import { groupBy, orderBy } from 'lodash';
import { ACCESS_TOKEN_NAME, ONBOARDING_WATCHLIST_NAME, REFRESH_TOKEN_NAME } from '../Constants';
import { EmailPreferences } from '../models/EmailPreferences';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const login = async (code, uriUrl) => {
  try {
    const { data } = await api.post('/authentication/v1/auth/connect', {
      applicationId: 'fci',
      code,
      // eslint-disable-next-line no-restricted-globals
      callbackUrl: `${location.protocol}//${location.hostname}/callback?url_params=${encodeURIComponent(uriUrl)}`,
    });

    const { accessToken, refreshToken } = data;
    if (accessToken && refreshToken) {
      localStorage.setItem(ACCESS_TOKEN_NAME, accessToken);
      localStorage.setItem(REFRESH_TOKEN_NAME, refreshToken);
      auth$.next(accessToken);

      return true;
    }
  } catch (e) {
    console.error(e);
  }

  return false;
};

export const fetchUserProfile = async (): Promise<Profile> => {
  const { userId } = getCurrentUser();
  const { data } = await api.get(`profile/v1/profiles/${userId}`);
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchRoles = async () => {
  const { data } = await api.get('profile/v1/profiles/lookups/roles');
  data.push({
    code: null,
    label: 'Other',
  });

  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchMarkets = async () => {
  const { data } = await api.get('profile/v1/markets');
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchUserInterests = async () => {
  const { userId } = getCurrentUser();
  const { data } = await api.get(`profile/v1/profiles/${userId}/interests`);
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const saveUserRole = async ({ code, freeText }) => {
  const { userId } = getCurrentUser();
  const { data } = await api.patch(`profile/v1/profiles/${userId}`, { role: { code, freeText } });
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const saveUserInterests = async (interests) => {
  const { userId } = getCurrentUser();
  const { data } = await api.patch(`profile/v1/profiles/${userId}/interests`, interests);
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchUserWatchlists = async () => {
  const { userId } = getCurrentUser();
  const { data } = await api.get(`profile/v1/profiles/${userId}/watchlists`);
  return orderBy(data, ['name']);
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchOnboardingWatchlists = async () => {
  const watchlists = await fetchUserWatchlists();
  const wl = watchlists.find((wl) => wl.name === ONBOARDING_WATCHLIST_NAME);

  if (wl) {
    const { data } = await api.post('common/v1/issuers', { watchlistId: wl.id });
    return {
      id: wl.id,
      mode: wl.mode,
      items: data.results.length > 0 ? data.results.concat([{}]) : [{}],
    };
  }
  return {
    id: undefined,
    mode: undefined,
    items: [{}],
  };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const saveWatchlist = async (id, watchlist) => {
  const { userId } = getCurrentUser();
  if (id) {
    const { data } = await api.patch(`profile/v1/profiles/${userId}/watchlists/${id}`, watchlist);
    return data;
  }
  const { data } = await api.post(`profile/v1/profiles/${userId}/watchlists`, watchlist);
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchUserConsents = async () => {
  const { userId } = getCurrentUser();
  const { data } = await api.get(`profile/v1/profiles/${userId}/consents`);
  const results: any[] = [];
  const groups = groupBy(data, 'configuration.id');

  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const id in groups) {
    const array = orderBy(groups[id], ['consentDate'], ['desc']);

    if (array?.length > 0) {
      results.push(array[0]);
    }
  }
  return results;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const saveUserConsents = async (status) => {
  const { userId } = getCurrentUser();
  await Promise.all(status.map((item) => api.post(`profile/v1/profiles/${userId}/consents`, item)));
  return { success: true };
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const fetchEmailPreferences = async () => {
  const { userId } = getCurrentUser();
  const { data } = await api.get<EmailPreferences>(`profile/v1/profiles/${userId}/email-preferences`);
  return data;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const saveEmailPreferences = async (preferences: EmailPreferences) => {
  const { userId } = getCurrentUser();
  const { data } = await api.patch<EmailPreferences>(`profile/v1/profiles/${userId}/email-preferences`, preferences);
  return data;
};
